import { Badge, Box, Flex, Popover, Text, TextArea, Tooltip } from '@radix-ui/themes';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { CSV_FILTER_RULES } from '~/containers/Sims/constants';
import { OnoBadge, OnoButton, OnoIcon } from '../Ono/elements';
import type { CSVInputProps, FilteredValue } from './types';

const VisualizerContainer = styled.div`
  max-height: 350px;
  overflow-y: auto;
  width: 100%;
`;

const StyledClearSelectedIcon = styled(OnoIcon)`
  position: absolute;
  top: 25px;
  right: 25px;
  z-index: 1;
`;

const PaddedTextArea = styled(TextArea)`
  padding-right: 20px;
  background-clip: unset;
`;

function castValueToString(value: string | string[] | undefined): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') return value;
  return '';
}

function getUniqueCleanedValues(values: FilteredValue[]): string[] {
  const cleanedValues = values.filter(item => !item.errorMessages || item.errorMessages.length === 0);
  const uniqueValues = Array.from(new Set(cleanedValues.map(item => item.value)));
  return uniqueValues;
}

function splitAndFilterValues(key: string, value: string | string[]): FilteredValue[] {
  const values = (Array.isArray(value) ? value : value.split(',')).map(item => item.trim()).filter(Boolean);

  const filterSchema = CSV_FILTER_RULES[key];
  if (!filterSchema) return values.map(value => ({ key, value }));

  return values.map(item => {
    const { error } = filterSchema.validate(item, { abortEarly: false });
    const errorMessages = error?.details.map(detail => detail.message);

    return { errorMessages, key, value: item };
  });
}

function ErrorHoverCardContent({ error }: { error: FilteredValue }) {
  const errorMessages = error.errorMessages?.map(message => (
    <Text
      key={message}
      size='1'
    >
      {message}
    </Text>
  ));

  return (
    <Flex
      direction='column'
      gap='1'
    >
      {errorMessages}
    </Flex>
  );
}

function CSVInput({
  csvKey,
  label,
  size,
  onChange,
  value,
  itemLimit = 5000,
  itemLength = 30,
  onRemove,
  defaultOpen,
}: CSVInputProps) {
  const [characterLimitReached, setCharacterLimitReached] = useState(false);
  const [itemLimitReached, setItemLimitReached] = useState(false);

  const [textAreaContent, setTextAreaContent] = useState<string>(castValueToString(value));
  const [selected, setSelected] = useState<FilteredValue[]>(splitAndFilterValues(csvKey, castValueToString(value)));
  const cleanedSelectedAmount = useMemo(() => getUniqueCleanedValues(selected).length, [selected]);
  const [open, setOpen] = useState(false);
  const blockCloseUntilRef = useRef(0);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const characterLimit = itemLimit * (itemLength + 2); // +1 for comma, +1 for space

  const setTextAreaRef = useCallback((node: HTMLTextAreaElement) => {
    textAreaRef.current = node;
    node?.focus();
  }, []);

  useEffect(() => {
    if (defaultOpen) {
      // Defer opening until the DropdownMenu's click event has finished propagating.
      // Then block closes for 200ms to absorb the spurious onPointerDownOutside /
      // onFocusOutside events that Radix fires as it returns focus to the
      // DropdownMenu trigger (via rAF / setTimeout) right as our Popover opens.
      const closeTimeoutId = setTimeout(() => {
        blockCloseUntilRef.current = Date.now() + 200;
        setOpen(true);
      }, 0);
      return () => clearTimeout(closeTimeoutId);
    }
  }, [defaultOpen]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: selected.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 28,
  });

  useEffect(() => {
    if (open) requestAnimationFrame(() => virtualizer.measure());
  }, [open, virtualizer]);

  function handleOnChange(valuesAsString: string) {
    setCharacterLimitReached(false);
    setItemLimitReached(false);
    setTextAreaContent(valuesAsString);

    const items = splitAndFilterValues(csvKey, valuesAsString);
    setSelected(items);

    if (items.length > itemLimit) return setItemLimitReached(true);
    if (valuesAsString.length > characterLimit) return setCharacterLimitReached(true);

    if (onChange) {
      onChange(getUniqueCleanedValues(items));
    }
  }

  function removeItem(item: FilteredValue) {
    const itemIndex = selected.findIndex(cv => cv.value === item.value);
    if (itemIndex === -1) return;

    setCharacterLimitReached(false);
    setItemLimitReached(false);

    const newSelected = [...selected];
    newSelected.splice(itemIndex, 1);

    const newSelectedAsString = newSelected.map(item => item.value).join(', ');

    setSelected(newSelected);
    setTextAreaContent(newSelectedAsString);

    if (newSelectedAsString.length > characterLimit) return setCharacterLimitReached(true);
    if (newSelected.length > itemLimit) return setItemLimitReached(true);

    if (onChange) {
      onChange(getUniqueCleanedValues(newSelected));
    }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={open => {
        if (Date.now() < blockCloseUntilRef.current && !open) return;
        setOpen(open);
      }}
    >
      <Popover.Trigger>
        <OnoButton
          color='gray'
          variant='outline'
        >
          <Text
            weight='regular'
            size={size}
            highContrast
            color='gray'
          >
            {label}
          </Text>
          <OnoBadge>
            <Text
              size={size}
              highContrast
              color='gray'
              weight='bold'
            >
              {cleanedSelectedAmount} selected
            </Text>
          </OnoBadge>
          <OnoIcon
            name='x-badge-size-3'
            width={16}
            height={16}
            inline
            onClick={e => {
              e.stopPropagation();
              onRemove?.();
            }}
            hoveredColor='var(--neutral-5)'
          />
        </OnoButton>
      </Popover.Trigger>
      <Popover.Content width='250px'>
        <Flex
          direction='column'
          gap='1'
        >
          <PaddedTextArea
            ref={setTextAreaRef}
            value={textAreaContent}
            onChange={e => handleOnChange(e.target.value)}
            onBlur={() => {
              if (Date.now() < blockCloseUntilRef.current) {
                setTimeout(() => textAreaRef.current?.focus(), 0);
              }
            }}
          />

          {textAreaContent && (
            <StyledClearSelectedIcon
              name='x-badge-size-3'
              width={16}
              height={16}
              inline
              onClick={() => {
                onChange?.([]);
                setSelected([]);
                setTextAreaContent('');
              }}
              hoveredColor='var(--neutral-5)'
            />
          )}
          {characterLimitReached && (
            <Text
              size='1'
              color='red'
            >
              Character limit of {characterLimit} exceeded. Please reduce the number of items to {itemLimit}.
            </Text>
          )}
          {itemLimitReached && (
            <Text
              size='1'
              color='red'
            >
              Item limit of {itemLimit} exceeded. Please reduce the number of items.
            </Text>
          )}
          <Text
            weight='regular'
            size='1'
            color='gray'
          >
            {label} filtering only returns exact matches. Separate multiple entries with a comma e.g., item1, item2,
            item3.
          </Text>
          <Flex
            direction='column'
            gap='1'
            align='start'
          >
            <VisualizerContainer ref={parentRef}>
              <Box style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualizer.getVirtualItems().map(vItem => {
                  const item = selected[vItem.index];
                  const isError = !!item.errorMessages && item.errorMessages.length > 0;

                  return (
                    <Box
                      key={vItem.key}
                      style={{ position: 'absolute', top: vItem.start, left: 0, width: '100%' }}
                    >
                      <Badge color={isError ? 'red' : 'gray'}>
                        {isError && (
                          <Tooltip content={<ErrorHoverCardContent error={item} />}>
                            <Box>
                              <OnoIcon
                                name='warning'
                                height={12}
                                width={12}
                                inline
                                color='var(--red-9)'
                              />
                            </Box>
                          </Tooltip>
                        )}
                        <Text
                          size='1'
                          color='gray'
                          weight='bold'
                        >
                          {item.value}
                        </Text>
                        <OnoIcon
                          name='x-badge-size-1-&-2'
                          height={12}
                          width={12}
                          inline
                          onClick={() => removeItem(item)}
                          hoveredColor='var(--neutral-7)'
                        />
                      </Badge>
                    </Box>
                  );
                })}
              </Box>
            </VisualizerContainer>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}

export default CSVInput;
